import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Book } from "../api";
import { Button, Card, Input, SectionHeading } from "../components/ui";
import { BookCover } from "../components/BookCover";

interface Candidate {
  title: string;
  author: string;
  isbn: string | null;
  coverUrl: string | null;
  pageCount: number | null;
  summary: string | null;
  source: "openlibrary";
  workKey: string | null;
}

export default function AddBookPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const search = useQuery({
    queryKey: ["book-search", submitted],
    queryFn: () => api.get<{ results: Candidate[] }>(`/books/search?q=${encodeURIComponent(submitted)}`),
    enabled: submitted.length > 0,
  });

  const add = useMutation({
    mutationFn: (c: Candidate) =>
      api.post<{ book: Book }>("/books", {
        title: c.title,
        author: c.author,
        isbn: c.isbn,
        coverUrl: c.coverUrl,
        pageCount: c.pageCount,
        summary: c.summary,
        workKey: c.workKey,
        setCurrent: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-book"] });
      qc.invalidateQueries({ queryKey: ["books"] });
      navigate("/");
    },
  });

  return (
    <div className="flex flex-col gap-10">
      <SectionHeading
        eyebrow="Choose your next read"
        title="Add a book"
        description="Search by title, author, or ISBN. We'll pull in the cover and details from OpenLibrary."
      />

      <Card>
        <form
          className="flex gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(query.trim());
          }}
        >
          <Input
            placeholder="The Goldfinch by Donna Tartt…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1"
            autoFocus
          />
          <Button type="submit" loading={search.isFetching}>
            Search
          </Button>
        </form>
      </Card>

      {search.data?.results.length === 0 && submitted ? (
        <p className="text-plum-soft text-center italic">No results for "{submitted}".</p>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {(search.data?.results ?? []).map((c, i) => (
          <Card key={`${c.workKey ?? c.title}-${i}`} className="flex gap-5 hover:shadow-[0_4px_8px_rgba(61,43,61,0.06),_0_24px_48px_rgba(199,123,133,0.14)] transition-shadow duration-300">
            <BookCover url={c.coverUrl} title={c.title} author={c.author} size="md" />
            <div className="flex-1 flex flex-col">
              <h3 className="font-serif text-xl text-plum leading-tight">{c.title}</h3>
              <p className="text-plum-soft text-sm mt-1">{c.author}</p>
              {c.pageCount ? (
                <p className="text-plum-mute text-xs mt-2 italic">{c.pageCount} pages</p>
              ) : null}
              <div className="mt-auto pt-4">
                <Button
                  size="sm"
                  variant="primary"
                  loading={add.isPending && add.variables === c}
                  onClick={() => add.mutate(c)}
                >
                  Set as current book
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {add.error ? (
        <p className="text-rose-dark text-sm text-center">{(add.error as Error).message}</p>
      ) : null}
    </div>
  );
}
